import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Users, ListChecks, Target, HelpCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PublicLayout from "@/components/layout/PublicLayout";
import SEO from "@/components/SEO";
import LeadForm from "@/components/LeadForm";
import { Reveal, Icon } from "@/components/motion";
import { api } from "@/lib/api";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setS(null); setNotFound(false);
    api.get(`/services/${slug}`).then((r) => setS(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <PublicLayout><div className="max-w-3xl mx-auto px-5 py-32 text-center"><h1 className="font-display text-3xl font-bold">Service not found</h1><Link to="/services" className="text-primary mt-4 inline-block">Back to services</Link></div></PublicLayout>;
  if (!s) return <PublicLayout><div className="max-w-3xl mx-auto px-5 py-32 text-center text-slate-400">Loading…</div></PublicLayout>;

  const schema = {
    "@context": "https://schema.org", "@type": "Service", name: s.title, description: s.short,
    provider: { "@type": "Organization", name: "NewStarAds" }, areaServed: "Worldwide",
  };
  const faqSchema = s.faqs?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: s.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  } : null;

  return (
    <PublicLayout>
      <SEO title={`${s.title} | NewStarAds`} description={s.short} schema={schema} />
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}

      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-12">
        <Link to="/services" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-6"><ArrowLeft className="w-4 h-4" /> All services</Link>
        <Reveal>
          <div className="w-14 h-14 grid place-items-center rounded-2xl bg-primary/10 text-primary mb-6"><Icon name={s.icon} className="w-7 h-7" /></div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">{s.title}</h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{s.what}</p>
        </Reveal>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 space-y-10">
        <Reveal className="rounded-2xl border border-slate-200 p-7">
          <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-bold">Who it's for</h2></div>
          <p className="text-slate-600 leading-relaxed">{s.who}</p>
        </Reveal>

        <Reveal className="rounded-2xl border border-slate-200 p-7">
          <div className="flex items-center gap-2 mb-4"><ListChecks className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-bold">How it works</h2></div>
          <ol className="space-y-3">
            {(s.how || []).map((step, i) => (
              <li key={i} className="flex gap-3"><span className="shrink-0 w-6 h-6 grid place-items-center rounded-full bg-primary text-white text-xs font-bold">{i + 1}</span><span className="text-slate-600">{step}</span></li>
            ))}
          </ol>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          <Reveal className="rounded-2xl border border-slate-200 p-7">
            <div className="flex items-center gap-2 mb-3"><Target className="w-5 h-5 text-primary" /><h2 className="font-display text-lg font-bold">Expected outcome</h2></div>
            <p className="text-slate-600 leading-relaxed text-sm">{s.outcome}</p>
          </Reveal>
          <Reveal delay={0.05} className="rounded-2xl border border-slate-200 p-7">
            <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="w-5 h-5 text-primary" /><h2 className="font-display text-lg font-bold">What we need from you</h2></div>
            <ul className="space-y-2">{(s.requirements || []).map((r, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />{r}</li>)}</ul>
          </Reveal>
        </div>

        {s.faqs?.length > 0 && (
          <Reveal>
            <div className="flex items-center gap-2 mb-4"><HelpCircle className="w-5 h-5 text-primary" /><h2 className="font-display text-xl font-bold">FAQs</h2></div>
            <Accordion type="single" collapsible className="rounded-2xl border border-slate-200 px-5">
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger><AccordionContent className="text-slate-600">{f.a}</AccordionContent></AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        )}
      </section>

      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl font-bold tracking-tight">Interested in {s.title}?</h2>
            <p className="mt-3 text-slate-600">Request a free consultation and we'll tell you honestly what's realistic for your goals and budget.</p>
          </div>
          <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <LeadForm compact defaultService={s.title} />
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
